import { type Bank, type BankConnection, type CurrencyEnum, type FinApiActionEnum, type MatchingDataInterface, type WebformResponse } from "@finway-group/shared/lib/models"
import axios, { type AxiosResponse } from "axios"
import type moment from "moment-timezone"
import { i18n } from "~Shared/locales/i18n"
import { openWindowInNewTab } from "~Shared/utils/window.utils"

const MODEL = "banking"

export const handleWebformResponse = (res: AxiosResponse<WebformResponse>) => {
    if (res.data.webFormUrl) {
        openWindowInNewTab({ url: res.data.webFormUrl, notificationUrlLinkDisplayText: i18n.t("notification:banking.webformurl.link") })
    }
    return res.data
}

export const BankService = {
    fetchBankConnectionById: async (id: string) => {
        const { data } = await axios.get(`/${MODEL}/${id}`)
        return data as BankConnection
    },
    fetchAllBankConnections: async () => {
        const { data } = await axios.get(`/${MODEL}`)
        return data as Array<BankConnection>
    },
    updateBankConnection: async (id: string, updateData: BankConnection) => {
        const { data } = await axios.put<BankConnection>(`/${MODEL}/${id}`, updateData)
        return data
    },
    deleteBankConnection: async (id: string) => axios.delete(`/${MODEL}/${id}`),
    deleteBankAccount: async (id: string) => axios.delete(`/${MODEL}/account/${id}`),
    performManualBankConnectionUpdate: async (connectionId: string) => axios.post<WebformResponse>(`${MODEL}/update`, { connectionId }).then(handleWebformResponse),
    importBankConnection: async (bankId: string) => axios.post<WebformResponse>(`/${MODEL}/import`, { bankId }).then(handleWebformResponse),
    issuePayment: async (accountId: string, expenseIds: Array<string>, executionDate?: string, paymentSubjects?: any, shouldBypassAmountCheck?: boolean) =>
        axios.post<WebformResponse>(`/${MODEL}/pay`, { accountId, expenseIds, executionDate, paymentSubjects, shouldBypassAmountCheck }).then(handleWebformResponse),
    searchBanks: async (searchTerm: string) => {
        const { data } = await axios.post(`/${MODEL}/search`, { searchTerm })
        return data as Array<Bank>
    },
    checkWebFormStatus: async (webFormId: string, action: FinApiActionEnum, data?: any | undefined) => {
        const res = await axios.post(`/${MODEL}/webform/${webFormId}`, { action, data })
        return res.data as BankConnection
    },
    matchBankTransactions: async (accountId: string) => {
        const {
            data: { matchingData },
        } = await axios.post(`/${MODEL}/match`, { accountId })
        return matchingData as Array<MatchingDataInterface>
    },
    revokeConsent: async (connectionId: string) => axios.post(`/${MODEL}/consent/revoke`, { connectionId }),
    fundWallet: async (accountId: string, amount: number, currency: CurrencyEnum, executionDate?: string) => {
        await axios.post<WebformResponse>(`/${MODEL}/fund-wallet`, { accountId, amount, currency, executionDate }).then(handleWebformResponse)
    },
    getCashBalanceForPeriod: async (startDate: moment.Moment, endDate: moment.Moment) => {
        const { data } = await axios.post(`/${MODEL}/balance`, { startDate: startDate.format("YYYY-MM-DD"), endDate: endDate.format("YYYY-MM-DD") })
        return data
    },
    refreshPaymentStatus: async (expenseId: string) => {
        const { data } = await axios.post(`/${MODEL}/payment-status`, { expenseId })
        return data
    },
    getBankAccounts: async () => {
        const { data } = await axios.get(`/${MODEL}/accounts/all`)
        return data
    },
}
